/**
 * LanguageSelector Component
 * Independent language selector for live session speech recognition.
 * This does NOT change the website language — it only controls which language
 * the speech recogniser uses during the live avatar session.
 *
 * Mirrors the mobile app's ChatInputBar language button behavior.
 */

import type React from 'react';
import { useTranslation } from 'react-i18next';
import { CoachMark } from '../CoachMark';
import './LanguageSelector.css';

/** STT language options — ordered: English first, then popular (Turkish, Arabic), then rest */
export const STT_LANGUAGES: { code: string; label: string; nativeName: string }[] = [
  { code: 'en-US', label: 'EN', nativeName: 'English' },
  { code: 'tr-TR', label: 'TR', nativeName: 'Türkçe' },
  { code: 'ar-SA', label: 'AR', nativeName: 'العربية' },
  { code: 'es-ES', label: 'ES', nativeName: 'Español' },
  { code: 'fr-FR', label: 'FR', nativeName: 'Français' },
  { code: 'de-DE', label: 'DE', nativeName: 'Deutsch' },
  { code: 'it-IT', label: 'IT', nativeName: 'Italiano' },
  { code: 'pt-PT', label: 'PT', nativeName: 'Português' },
  { code: 'ru-RU', label: 'RU', nativeName: 'Русский' },
  { code: 'zh-CN', label: 'ZH', nativeName: '中文' },
  { code: 'ja-JP', label: 'JA', nativeName: '日本語' },
  { code: 'ko-KR', label: 'KO', nativeName: '한국어' },
  { code: 'ur-PK', label: 'UR', nativeName: 'اردو' },
  { code: 'id-ID', label: 'ID', nativeName: 'Bahasa' },
  { code: 'fa-IR', label: 'FA', nativeName: 'فارسی' },
  { code: 'hi-IN', label: 'HI', nativeName: 'हिन्दी' },
  { code: 'bn-BD', label: 'BN', nativeName: 'বাংলা' },
];

interface LanguageSelectorProps {
  /** Currently selected BCP-47 language code */
  value: string;
  /** Called when the user picks a new language */
  onChange: (languageCode: string) => void;
  className?: string;
  variant?: 'mobile' | 'desktop';
  disabled?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  className = '',
  variant = 'desktop',
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <CoachMark
      id="stt-language"
      message={t(
        'chat.languageCoachMark',
        'Select your preferred language before starting a live session.',
      )}
      buttonText={t('chat.gotIt', 'Got it')}
      position="top"
    >
      <div
        className={`language-selector-wrapper ${variant} ${disabled ? 'disabled' : ''} ${className}`}
      >
        <svg
          className="globe-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          role="img"
          aria-label="Globe icon"
        >
          <title>Globe icon</title>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="language-selector-select"
          title="Speech Language"
          id="stt-language-selector"
          disabled={disabled}
        >
          {STT_LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label} ({lang.nativeName})
            </option>
          ))}
        </select>
      </div>
    </CoachMark>
  );
};
